import React, { Suspense } from 'react';
import { Skeleton } from '@chakra-ui/react';
import lazyWithRetry from '../../utils/lazyWithRetry';

// item 1515 (code splitting) — the LAZY FACADE for the markdown editor. @uiw/react-md-editor
// (+ react-markdown, remark/rehype, prism, rehype-sanitize) is the heaviest thing a document form
// pulls in, and most visits never open a scope/terms editor — so the implementation
// (RichTextEditorImpl) lives in its own chunk and loads on first mount. Every existing import site
// (MarkdownTextInput, Settings, contract templates, ProposalForm's RichTextView, the public
// proposal page) keeps the same props. Stale-tab chunk 404s self-heal once via lazyWithRetry.
const LazyEditor = lazyWithRetry(() => import(/* webpackChunkName: "md-editor" */ './RichTextEditorImpl'));
const LazyView = lazyWithRetry(() => import(/* webpackChunkName: "md-editor" */ './RichTextEditorImpl')
  .then((m) => ({ default: m.RichTextView })));

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  height?: number;
  placeholder?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value, onChange, height, placeholder,
}) => (
  // the placeholder keeps the form's height so nothing jumps when the chunk lands
  <Suspense fallback={<Skeleton w="full" height={`${height ?? 280}px`} borderRadius="md" />}>
    <LazyEditor value={value} onChange={onChange} height={height} placeholder={placeholder} />
  </Suspense>
);

RichTextEditor.defaultProps = {
  height: 280,
  placeholder: 'Write here… use the toolbar to format.',
};

// Render markdown content read-only (proposal preview, etc.) — same lazy chunk.
export const RichTextView: React.FC<{ source?: string }> = ({ source }) => (
  <Suspense fallback={<Skeleton w="full" height="48px" borderRadius="md" />}>
    <LazyView source={source} />
  </Suspense>
);

RichTextView.defaultProps = { source: '' };

export default RichTextEditor;
